import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { cn } from '../../../lib/cn';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  /** 補足説明（メッセージ下に小さく表示）。 */
  description?: string;
  /** 設定するとトースト本体がクリック可能になり、クリックで実行後に自動で閉じる。 */
  onClick?: () => void;
}

/** @graph-connects none */
const DEFAULT_TOAST_DURATION = 5_000;

/** showToast のオプション。 */
export interface ShowToastOptions {
  /** 自動で閉じるまでの時間 (ms)。デフォルトは 5 秒。 */
  duration?: number;
  /** 補足説明（メッセージ下に小さく表示）。 */
  description?: string;
  /** 設定するとトースト本体がクリック可能になり、クリックで実行後に自動で閉じる。 */
  onClick?: () => void;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (type: ToastType, message: string, options?: ShowToastOptions) => void;
  dismissToast: (id: string) => void;
}

interface ToastProviderProps {
  children: ReactNode;
  idGenerator?: () => string;
}

/** @graph-connects none */
const ToastContext = createContext<ToastContextType | null>(null);

/** @graph-connects none */
const typeStyles = {
  success:
    'bg-[var(--color-toast-success-bg)] border-[var(--color-toast-success-border)] text-[var(--color-toast-success-icon)]',
  error:
    'bg-[var(--color-toast-error-bg)] border-[var(--color-toast-error-border)] text-[var(--color-toast-error-icon)]',
  warning:
    'bg-[var(--color-toast-warning-bg)] border-[var(--color-toast-warning-border)] text-[var(--color-toast-warning-icon)]',
  info: 'bg-[var(--color-toast-info-bg)] border-[var(--color-toast-info-border)] text-[var(--color-toast-info-icon)]',
};

/** @graph-connects none */
const messageStyles = {
  success: 'text-[var(--color-toast-success-text)]',
  error: 'text-[var(--color-toast-error-text)]',
  warning: 'text-[var(--color-toast-warning-text)]',
  info: 'text-[var(--color-toast-info-text)]',
};

/** @graph-connects none */
const descriptionStyles = {
  success: 'text-[var(--color-toast-success-description)]',
  error: 'text-[var(--color-toast-error-description)]',
  warning: 'text-[var(--color-toast-warning-description)]',
  info: 'text-[var(--color-toast-info-description)]',
};

/** @graph-connects none */
const dismissButtonStyles = {
  success: 'hover:bg-[var(--color-toast-success-hover)]',
  error: 'hover:bg-[var(--color-toast-error-hover)]',
  warning: 'hover:bg-[var(--color-toast-warning-hover)]',
  info: 'hover:bg-[var(--color-toast-info-hover)]',
};

/** @graph-connects none */
const typeIcons = {
  success: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

let fallbackToastId = 0;

/** @graph-connects none */
function defaultToastIdGenerator(): string {
  fallbackToastId += 1;
  return globalThis.crypto?.randomUUID?.() ?? `toast-${String(fallbackToastId)}`;
}

/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business 個別トースト通知アイテム。種別アイコン・メッセージ・閉じるボタンをアニメーション付きで表示
 * @graph-connects none
 */
function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const body = (
    <>
      <p className={cn('text-sm font-semibold', messageStyles[toast.type])}>{toast.message}</p>
      {toast.description != null && toast.description !== '' && (
        <p className={cn('text-xs', descriptionStyles[toast.type])}>{toast.description}</p>
      )}
    </>
  );
  return (
    <div
      role="alert"
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-lg border backdrop-blur-sm shadow-lg animate-slide-in-right',
        typeStyles[toast.type],
      )}
    >
      {typeIcons[toast.type]}
      {toast.onClick !== undefined ? (
        <button
          type="button"
          onClick={() => {
            toast.onClick?.();
            onDismiss();
          }}
          className="min-w-0 flex-1 cursor-pointer rounded text-left transition-colors hover:opacity-80"
        >
          {body}
        </button>
      ) : (
        <div className="min-w-0 flex-1">{body}</div>
      )}
      <button
        onClick={onDismiss}
        className={cn('p-1 rounded transition-colors', dismissButtonStyles[toast.type])}
        aria-label="閉じる"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
}

/**
 * @graph-stack web-design-system
 * @graph-domain Engineering
 * @graph-business トースト通知プロバイダー。success/error/warning/info型の一時通知をContext経由で子コンポーネントに提供し、右下にスタック表示
 * @graph-connects none
 */
export function ToastProvider({
  children,
  idGenerator = defaultToastIdGenerator,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (type: ToastType, message: string, options?: ShowToastOptions) => {
      const id = idGenerator();
      const toast: Toast = {
        id,
        type,
        message,
        duration: options?.duration,
        description: options?.description,
        onClick: options?.onClick,
      };

      setToasts((prev) => [...prev, toast]);

      const duration = options?.duration ?? DEFAULT_TOAST_DURATION;
      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
      }
    },
    [idGenerator],
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      {/* Toast Container */}
      <div
        className="fixed bottom-4 right-4 z-[var(--z-toast)] flex flex-col gap-2 max-w-sm w-full pointer-events-none"
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem
              toast={toast}
              onDismiss={() => {
                dismissToast(toast.id);
              }}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * @graph-stack web-design-system
 * @graph-domain Engineering
 * @graph-business トースト通知フック。ToastProviderのContextからshowToast/dismissToastメソッドを取得するアクセサ
 * @graph-connects none
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
